'use client'
import { FC, useState, useEffect, useRef } from "react";
import { Content, isFilled } from "@prismicio/client";
import { asLink } from "@prismicio/helpers";
import { useForm } from "react-hook-form";
import ReCAPTCHA from "react-google-recaptcha";
import { SliceComponentProps, PrismicRichText } from "@prismicio/react";
import { usePathname } from 'next/navigation'

/**
 * Props for `ContactForm`.
 */
export type ContactFormProps = SliceComponentProps<Content.ContactFormSlice>;

/**
 * Component for "ContactForm" Slices.
 */
const ContactForm: FC<ContactFormProps> = ({ slice }) => {

    const { register, handleSubmit, setValue, formState: { errors }  } = useForm();
    const [success, setSuccess] = useState(false) as any
    const [errorMessage, setErrorMessage]= useState(null) as any
    

    const onSubmit = async (data: any) => {
        const EMAIL_TO = slice.primary.email_to;
        const UPLOAD_URL = slice.primary.form_action as string
        const REDIRECT_URL = slice.primary.redirect_page
        const redirectUrl = isFilled.link(REDIRECT_URL) ? asLink(REDIRECT_URL) : null
        setErrorMessage(null)
        await fetch(UPLOAD_URL, { method:"post", body: JSON.stringify({data: data, sendto: EMAIL_TO}), headers: {
            'Content-Type': 'application/json'
        } } ).then(res => res.json())
        .then(response => { 
            if(response.status==="success") {
                setSuccess(true);
                if(redirectUrl) {
                    window.location.href = redirectUrl;
                }
            } else {
                setErrorMessage("Sorry, something went wrong. Please try again.");
            }
        })
        .catch(error => {setErrorMessage(error.status)});
    };

    const recaptchaRef = useRef(null) as any
    const pageUrl = usePathname();

    useEffect(()=>{
        const searchParams = new URLSearchParams(window.location.search);
        const place_id = searchParams.get('place_id');
        const address = searchParams.get('address');
        setValue('place_id', place_id);
        setValue('address', address);
        setValue('subject', "Update Address Details");
    },[setValue])

    const required = "";
    const submit = "border-jade border-[1px] text-jade hover:text-white hover:bg-jade  rounded-full h-[56px] inline-flex px-8 items-center justify-center duration-300 cursor-pointer mb-16"
    const standard = "text-[18px] bg-white px-4 py-5 mt-2 w-full appearance-none outline-none focus:ring-[1px] focus:ring-jade mb-4" 

    return (
        <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="font-fellix "
        >
        <div className={"px-4 md:px-8 lg:px-12 xl:px-0 "+slice.primary.background.toLowerCase()}>
                <div className="max-w-[880px] mx-auto">
                    <article className="text-[28px] leading-[32px] lg:text-[40px] lg:leading-[48px] text-center max-w-[600px] mx-auto font-semibold pt-24 mb-16"><PrismicRichText field={slice.primary.heading} /></article>
                    <article className="prose prose-h6:m-0 prose-h6:mb-6 prose-h6:text-[28px] prose-h6:leading-[32px] prose-a:text-blue-500 prose-a:no-underline prose-a:hover:underline prose-a:cursor-pointer text-center max-w-[650px] mx-auto pb-16">{errorMessage ? <span className="text-red-500 text-[20px]">{errorMessage}</span> : <PrismicRichText field={slice.primary.intro_text} />}</article>
                    {success === false || success == null ?
                        <div className="form-area">
                            
                            <form onSubmit={handleSubmit(onSubmit)}>
                                {pageUrl !== "/lfc-event-2026" && (
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    sitekey={pageUrl === "lfc-event-2026" ? "6LcNmyUsAAAAAAgHlizFtrmoTwLbQnlSWq3oRQbe" : "6LeHB_sgAAAAAIDMta57NCT9bCSKTJPp3Vdy1GRO"}
                                    size="invisible"
                                    // onChange={(e:any)=>onChange(e)}
                                    // asyncScriptOnLoad={this.asyncScriptOnLoad}
                                />)}
                                <div className="grid grid-cols-12 gap-0 md:gap-8">
                                {slice.primary.formfields.map((item: any, index: number) => {
                                    // console.log(item)
                                    if(item.field_name) {
                                        switch(item.field_type) {
                                            case "textarea":
                                                return (
                                                    <div className={item.half_row === true ? "col-span-12 md:col-span-6" : "col-span-12"} key={index} >
                                                        <label >
                                                            <PrismicRichText field={item.field_title}/>
                                                            <textarea  className={item.field_required_ ===true ? required : standard} rows={8} {...register(item.field_name, {required: item.field_required_})}></textarea>
                                                        </label>
                                                    </div>
                                                )
                                            break;
                                            case "text":
                                                return (
                                                    <div className={item.half_row === true ? "col-span-12 md:col-span-6" : "col-span-12"} key={index} >
                                                        <label >
                                                            <PrismicRichText field={item.field_title}/>
                                                            <input type={item.field_type} className={item.field_required_ ===true ? required : standard} {...register(item.field_name, {required: item.field_required_})}/>
                                                        </label>
                                                    </div>
                                                    )
                                            break;
                                            case "checkbox":
                                                return (
                                                    <div className={item.half_row === true ? "col-span-12 md:col-span-6" : "col-span-12"} key={index} >
                                                        <label>
                                                            <div className="grid grid-cols-12">
                                                                <div className="col-span-1">
                                                                    <input type={item.field_type} className={`${item.field_required_ ===true ? required : ''} `} {...register(item.field_name, {required: item.field_required_})}/>
                                                                </div>
                                                                <div className="col-span-11 check prose-a:text-blue">
                                                                    <PrismicRichText field={item.field_title} {...register(item.field_name, {required: item.field_required_})}/>
                                                                </div>
                                                            </div>
                                                        </label>
                                                    </div>
                                                    )
                                            break;
                                            case "hidden":
                                                return (
                                                    <input type={item.field_type} {...register(item.field_name)} key={index}/>
                                                    )
                                            break;
                                            case "number":
                                                return (
                                                    <div className={item.half_row === true ? "col-span-12 md:col-span-6" : "col-span-12"} key={index} >
                                                        <label >
                                                            <PrismicRichText field={item.field_title}/>
                                                            <input type={item.field_type} className={item.field_required_ ===true ? required : standard} {...register(item.field_name, {required: item.field_required_})}/>
                                                        </label>
                                                    </div>
                                                    )
                                            break;
                                            case "email":
                                                return (
                                                    <div className={item.half_row === true ? "col-span-12 md:col-span-6" : "col-span-12"} key={index} >
                                                        <label >
                                                            <PrismicRichText field={item.field_title}/>
                                                            <input type={item.field_type} className={item.field_required_ ===true ? required : standard} {...register(item.field_name, {required: item.field_required_})}/>
                                                        </label>
                                                    </div>
                                                    )
                                            break;
                                            // case "file":
                                            //     return (
                                            //         <div className={item.half_row === true ? "col-span-6" : "col-span-12"} key={index} >
                                            //             <label >
                                            //                 {fileError || errorMessage ?
                                            //                     <p className="error">{fileError}{errorMessage}</p>
                                            //                 : 
                                            //                     <PrismicRichText field={item.field_title}/> 
                                            //                 }
                                            //                 <br/>
                                            //                 <input ref={fileUploadRef} type={item.field_type} name={item.field_name} accept="" multiple={true} className={item.field_required_ === true ? required : standard} onChange={(e)=> handleFileChange(e)}/>
                                            //             </label>
                                            //             <br/>
                                            //             <br/>
                                            //         </div>
                                            //         )
                                            //     return null
                                            // break;
                                            case "select":
                                                const selectOptions = item.options.split(",");
                                                return (
                                                    <div className={item.half_row === true ? "col-span-12 md:col-span-6" : "col-span-12"} key={index} >
                                                        <label >
                                                            <PrismicRichText field={item.field_title}/>
                                                            <select className={item.field_required_ ===true ? required : standard} {...register(item.field_name, {required: item.field_required_})}>
                                                                {selectOptions.map((option: any, index: number)=>{
                                                                    return (
                                                                        <option value={option} key={index}>{option}</option>
                                                                    )
                                                                })}
                                                            </select>
                                                        </label>
                                                    </div>
                                                    )
                                            break;
                                            // case "submit":
                                            //     return (
                                            //         <div className={item.half_row === true ? "col-span-6" : "col-span-12"} key={index} >
                                            //             <input type={item.field_type} name={item.field_name} />
                                            //         </div>
                                            //         )
                                            // break;
                                            // default:
                                        }
                                    }
                                    else {
                                        return null
                                    }
                                    
                                })}
                                <div>
                                
                                </div>
                                </div>
                                <div className="flex justify-center lg:justify-end w-full pb-16 pt-12 md:pt-0">
                                {Object.keys(errors).length > 0 ? <p className="danger">Sorry, please check that all fields are filled out correctly</p> : <input className={submit} type="submit" name="submit"/> }
                                </div>
                            </form>
                        </div>
                        :   
                            <div className="success">
                                <h3 className="block text-center pb-24 text-[34px] font-semibold">Thank you for contacting us</h3>
                            </div> 
                        }    
                    </div>
                </div>
        </section>
    );
};

export default ContactForm;
